/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import fhirpath from 'fhirpath';

import { FhirPedidoType } from '@/features/enterprise/models/types';
import IntegracaoService from '@/features/enterprise/services/IntegracaoService';

import FormExame from '../FormExame';
import {
  Actions,
  CollpsableContent,
  Container,
  Header,
  HeaderTitle,
  HeaderSubTitle,
  EditButton,
  ExpandButton,
  EditedLabel,
  EditTools,
} from './styles';

interface BoxExameEditavelProps {
  exame: FhirPedidoType;
  detailsOpened?: boolean;
  disabled: boolean;
}

const BoxExameEditavel: React.FC<BoxExameEditavelProps> = ({
  exame,
  detailsOpened,
  disabled,
}) => {
  const subtitleRef = React.useRef<HTMLHeadingElement>(null);
  const editableContentRef = React.useRef<HTMLDivElement>(null);

  const [isOpenForEdit, setIsOpenForEdit] = React.useState(
    detailsOpened || false,
  );
  const [isSubtitleExpanded, setIsSubtitleExpanded] = React.useState(false);
  const [exameEditavel, setExameEditavel] = React.useState<FhirPedidoType>(
    exame,
  );
  const [isSubtitleExpandable, setSubtitleExpandable] = React.useState(false);
  const [loadingConfirmation, setloadingConfirmation] = React.useState(false);

  const nome = React.useMemo(() => {
    return fhirpath.evaluate(
      exameEditavel,
      'ServiceRequest.code.text | ServiceRequest.code.coding.display',
    )[0];
  }, [exameEditavel]);

  const comentario = React.useMemo(() => {
    const note = fhirpath.evaluate(exameEditavel, 'ServiceRequest.note.text');
    if (note.length) {
      return note[0];
    }

    return '';
  }, [exameEditavel]);

  const edited = React.useMemo(() => !!exameEditavel.reportedBoolean, [
    exameEditavel.reportedBoolean,
  ]);

  const canExpandSubtitle = React.useMemo(
    () => comentario && isSubtitleExpandable && !isOpenForEdit,
    [comentario, isOpenForEdit, isSubtitleExpandable],
  );

  const onEditClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
      event.stopPropagation();

      setIsOpenForEdit(true);
    },
    [],
  );

  const toggleCollapse = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      // If disabled, collapse should display the edit form
      if (disabled) {
        setIsOpenForEdit(prev => !prev);
      }

      setIsSubtitleExpanded(prev => !prev);
    },
    [disabled],
  );

  const onEditItem = React.useCallback(async (item: fhir4.DomainResource) => {
    try {
      setloadingConfirmation(true);
      const result = await IntegracaoService.updateFhirResource(
        item as FhirPedidoType,
      );

      if (result) {
        setIsOpenForEdit(false);
        setIsSubtitleExpanded(false);
        setExameEditavel(result);
      }
    } finally {
      setloadingConfirmation(false);
    }
  }, []);

  const onEditCancel = React.useCallback(async (item: fhir4.DomainResource) => {
    setExameEditavel(item as FhirPedidoType);
    setIsOpenForEdit(false);
    setIsSubtitleExpanded(false);
  }, []);

  React.useEffect(() => {
    if (subtitleRef.current) {
      setSubtitleExpandable(
        subtitleRef.current.scrollHeight > subtitleRef.current.clientHeight,
      );
    }
  }, []);

  return (
    <Container detailsOpen={isOpenForEdit || isSubtitleExpanded}>
      <Header>
        <HeaderTitle>{nome}</HeaderTitle>

        <Actions>
          <EditTools>
            {edited && <EditedLabel>editado</EditedLabel>}
            {!disabled && (
              <EditButton onClick={onEditClick} disabled={isOpenForEdit} />
            )}
          </EditTools>

          <ExpandButton
            disabled={!(disabled || canExpandSubtitle)}
            collapsed={isSubtitleExpanded}
            onClick={toggleCollapse}
          />
        </Actions>
      </Header>

      <HeaderSubTitle
        ref={subtitleRef}
        highlight={isSubtitleExpanded || isOpenForEdit}
      >
        {comentario}
      </HeaderSubTitle>

      <CollpsableContent ref={editableContentRef} highlight={isOpenForEdit}>
        <FormExame
          resource={exameEditavel as fhir4.ServiceRequest}
          onSubmit={onEditItem}
          onCancel={onEditCancel}
          loading={loadingConfirmation}
          readOnly={disabled}
        />
      </CollpsableContent>
    </Container>
  );
};

export default BoxExameEditavel;
